import { toBoundingBox, toBounds, unionBounds } from '@/utils/bounds-kit';
import type { BoundingBox, Bounds } from '@/version-types';
import {
    BLUR_EXTENT_SIGMAS,
    GLOW_BLUR_SIGMA_RATIO,
    GLOW_DILATE_RATIO,
    GLOW_MIN_BLUR_SIGMA,
    SHADOW_BLUR_SIGMA_RATIO,
} from '../../constants';
import type { TextRenderOutput } from '../../types';
import { translateBounds, unionLineBounds } from '../../utils';
import type { ArtTextVisual } from '../art-text';
import type { RenderContext } from '../context';
import type { TextFilter } from '../filter';
import { unionGlyphBounds, type GlyphStroke, type GlyphVisual } from '../glyph';
import type { HighlightVisual } from '../highlight';
import type { ListMarkerVisual } from '../list-marker';
import { resolveBodyOffset, resolveMaterialOffset } from './svg';

/**
 * 同一轮 Text Render Output 必须成组发布的完整几何事实。
 */
type TextRenderOutputGeometry = Pick<
    TextRenderOutput,
    'layoutBox' | 'renderBox' | 'offset' | 'exportRenderBox' | 'exportOffset'
>;

/**
 * 按普通文字 effect 规则扩展指定视觉边界。
 *
 * @param bounds - 尚未包含描边、阴影与发光的视觉边界。
 * @param strokes - 当前累计描边事实。
 * @param filterState - 当前阴影与外发光事实。
 * @returns 覆盖全部文字 effects 的边界。
 */
const expandEffectBounds = (bounds: Bounds, strokes: readonly GlyphStroke[], filterState: TextFilter): Bounds => {
    const stroke = strokes.at(-1)?.width ?? 0;
    // 阴影滤镜的标准差 σ = blur × 0.5；边界预留 3σ，再结合 x/y 偏移，避免预览和导出裁切阴影
    const shadowBlur = Math.max(0, filterState.shadow?.blur ?? 0) * SHADOW_BLUR_SIGMA_RATIO * BLUR_EXTENT_SIGMAS;
    const shadowTop = filterState.shadow ? Math.max(0, shadowBlur - filterState.shadow.y) : 0;
    const shadowRight = filterState.shadow ? Math.max(0, filterState.shadow.x + shadowBlur) : 0;
    const shadowBottom = filterState.shadow ? Math.max(0, filterState.shadow.y + shadowBlur) : 0;
    const shadowLeft = filterState.shadow ? Math.max(0, shadowBlur - filterState.shadow.x) : 0;
    // 覆盖发光扩张半径与 3σ 模糊范围，避免预览和导出裁切光晕
    const glow = filterState.glow
        ? filterState.glow.width * GLOW_DILATE_RATIO +
          Math.max(filterState.glow.width * GLOW_BLUR_SIGMA_RATIO, GLOW_MIN_BLUR_SIGMA) * BLUR_EXTENT_SIGMAS
        : 0;
    return {
        left: bounds.left - stroke - Math.max(shadowLeft, glow),
        top: bounds.top - stroke - Math.max(shadowTop, glow),
        right: bounds.right + stroke + Math.max(shadowRight, glow),
        bottom: bounds.bottom + stroke + Math.max(shadowBottom, glow),
    };
};

/**
 * 解析排版前后的逻辑区域。
 *
 * @param input - 当前 Text Render 输入。
 * @param snapshot - 当前排版 snapshot。
 * @returns 不含视觉 effects 的逻辑区域。
 */
const resolveLayoutBox = (context: RenderContext): BoundingBox => {
    const { input, snapshot } = context;
    const isHorizontal = snapshot.writingMode === 'horizontal-tb';
    const width = isHorizontal
        ? (input.width ?? snapshot.contentWidth + snapshot.listMarkerIndent)
        : snapshot.contentWidth;
    const height = isHorizontal
        ? snapshot.contentHeight
        : (input.height ?? snapshot.contentHeight + snapshot.listMarkerIndent);
    return { left: isHorizontal ? 0 : -width, top: 0, width, height };
};

type GeometryFeatures = Readonly<{
    glyph: GlyphVisual;
    filter: TextFilter;
    artText?: ArtTextVisual;
    highlight?: HighlightVisual;
    listMarker?: ListMarkerVisual;
}>;

/**
 * 一次解析完整 Text Render Output geometry。
 *
 * 运行时 geometry 覆盖完整 Layout Box 和全部可见内容；导出 geometry 默认只覆盖真实可见内容，存在
 * 可见背景时才补入完整 Layout Box。两套 offset 与各自 Render Box 在同一结果中生成，避免调用方混配。
 * 正文 path 边界直接消费 GlyphVisual 中本轮已经解析的 bounds，不再从 snapshot instructions 重复
 * 解析 SVG path。
 *
 * @param context - 当前一轮不变的输入、snapshot 与 SVG 命名空间。
 * @param features - 已准备好的描边、filter 与高级视觉边界。
 * @returns 同一轮生成的 Layout Box、运行时与导出 Render Box 及其 offset。
 */
export const foldGeometry = (context: RenderContext, features: GeometryFeatures): TextRenderOutputGeometry => {
    const { input, snapshot } = context;
    const layoutBox = resolveLayoutBox(context);
    const layoutBounds = toBounds(layoutBox);
    const bodyOffset = resolveBodyOffset(snapshot);
    const materialOffset = resolveMaterialOffset(snapshot);
    const plainContentBounds = unionBounds(
        unionLineBounds(snapshot.lineMetrics),
        unionGlyphBounds(features.glyph.items),
    );
    const translatedContentBounds = translateBounds(
        unionBounds(plainContentBounds, features.artText?.paintBounds),
        bodyOffset,
    );
    // 字符 marker 与正文共享 filter，使用真实 marker 边界参与 effects；图形型 marker 保持在 filter 外。
    const characterMarkerBounds = features.listMarker?.markerPaths.length ? features.listMarker.bounds : null;
    const contentBounds = unionBounds(translatedContentBounds, characterMarkerBounds);
    const materialBounds = translateBounds(
        unionBounds(features.artText?.materialBounds, features.highlight?.bounds),
        materialOffset,
    );
    const graphicMarkerBounds = features.listMarker?.drawGraphic ? features.listMarker.bounds : null;
    const advancedBounds = unionBounds(materialBounds, graphicMarkerBounds);

    // 运行时必须覆盖完整逻辑区域，再统一应用正文 effects，保持现有 Preview 与 drawable 外扩语义。
    const visualBounds = unionBounds(unionBounds(layoutBounds, contentBounds), advancedBounds) ?? layoutBounds;
    const renderBox = toBoundingBox(expandEffectBounds(visualBounds, features.glyph.strokes, features.filter));

    // 高级视觉位于 effects 容器外；导出只扩展正文，再与高级视觉和可见背景分别求并集。
    const effectedContentBounds = contentBounds
        ? expandEffectBounds(contentBounds, features.glyph.strokes, features.filter)
        : null;
    let exportBounds = unionBounds(effectedContentBounds, advancedBounds);
    if (input.backgrounds?.some((background) => !background.hidden)) {
        exportBounds = unionBounds(exportBounds, layoutBounds);
    }
    const exportRenderBox = toBoundingBox(exportBounds ?? layoutBounds);

    return {
        layoutBox,
        renderBox,
        offset: Object.freeze({ x: -renderBox.left, y: -renderBox.top }),
        exportRenderBox,
        exportOffset: Object.freeze({ x: -exportRenderBox.left, y: -exportRenderBox.top }),
    };
};
